import "./touchnumbers.scss";
import * as React from "react";
import withMediaQuery from "../../../developer/Utilities/MediaQuery";
import { getCurrencySymbol, isCurrencySymbolBeforeNumbers } from "../../Utilities/Generic/Numbers";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import Button from "../Buttons/Button";
import TouchNumber from "./TouchNumber";

export type BoundsType = false | number;

export interface TouchNumbersProps {
    value: string;
    bounds?: {
        max: BoundsType;
        min: BoundsType;
        decimalPlaces?: number;
    };
    input?: "percentage" | "currency" | "normal";
    onChange: (value: string) => void;
    onConfirm: (value: string) => void;
    onEscape?: (value: string) => void;
    hideConfirm?: boolean;
    increment?: boolean;
    subtract?: boolean;
    onRef?: (reference: undefined | TouchNumbers) => void;
    autoFocus?: boolean;
    static?: boolean;
    confirmText?: string;
    negative?: boolean;
}

interface Props extends TouchNumbersProps {
    matchesMedia: boolean;
}

interface State {
    focused: boolean;
    negative: boolean;
    value: string;
}

function prepareState(propVal: string | number, stateVal: string): {
    negative: boolean;
    value: string;
} {
    let negative = false;
    let value = propVal;

    if(typeof value === "number") {
        if(parseFloat(stateVal) !== propVal) {
            value = value.toString();
        } else {
            value = stateVal;
        }
    } else if(!value) {
        value = "";
    }

    if(value.charAt(0) === "-") {
        negative = true;
        value = value.slice(1);
    }

    return {
        negative,
        value,
    };
}

export class TouchNumbers extends React.Component<Props, State> {
    public static defaultProps = {
        confirmText: "Confirm",
        input      : "normal" as const,
        autoFocus  : true,
        increment  : false,
        subtract   : false,
        hideConfirm: false,
        bounds     : {
            max: false as const,
            min: false as const,
        },
    };

    protected inputRef: React.RefObject<HTMLInputElement | null>;
    protected resetValue: boolean;

    constructor(props: Props) {
        super(props);

        const { value, negative } = prepareState(props.value, "");

        this.state = {
            focused: false,
            negative,
            value,
        };

        if(props.bounds) {
            if(typeof props.bounds.max === "number" && typeof props.bounds.min === "number") {
                if(props.bounds.max < props.bounds.min) {
                    throw new Error(
                        "TouchNumbers bounds cannot be set to an invalid range\nMax: " +
                        props.bounds.max +
                        " Min: " +
                        props.bounds.min
                    );
                }
            }
        }

        this.handleFocus = this.handleFocus.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleButton = this.handleButton.bind(this);
        this.handleClear = this.handleClear.bind(this);
        this.captureClick = this.captureClick.bind(this);
        this.handleConfirm = this.handleConfirm.bind(this);
        this.handleDecrement = this.handleDecrement.bind(this);
        this.handleIncrement = this.handleIncrement.bind(this);
        this.handleButtonDown = this.handleButtonDown.bind(this);

        this.dispatchChange = this.dispatchChange.bind(this);

        this.inputRef = React.createRef();
        this.resetValue = false;
    }

    public componentDidMount(): void {
        if(typeof this.props.onRef === "function") {
            this.props.onRef(this);
        }

        requestAnimationFrame(() => {
            if(this.inputRef.current && this.props.autoFocus) {
                this.inputRef.current.focus();
                this.inputRef.current.select();
            }
        });
    }

    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        if(this.props.negative === prevProps.negative && this.props.value === prevProps.value) {
            return;
        }

        const { value, negative } = prepareState(this.props.value, this.state.value);

        if(value !== prevState.value) {
            this.setState({
                value,
            });
        }

        if(negative !== prevState.negative) {
            this.setState({
                negative,
            });
        }
    }

    public componentWillUnmount(): void {
        if(typeof this.props.onRef === "function") {
            this.props.onRef(undefined);
        }
    }

    public focus(): void {
        if(this.inputRef.current) {
            this.inputRef.current.focus();
        }
    }

    protected handleFocus(): void {
        this.setState({
            focused: true,
        });
    }

    protected handleBlur(): void {
        this.setState({
            focused: false,
        });
    }

    protected mapToBounds(value: string | number): string {
        // Check if bounds are set
        if(!this.props.bounds) {
            return value.toString();
        }

        // Parse value
        let parsed = value;

        if(typeof parsed === "string") {
            if(value === "") {
                return value;
            }

            parsed = parseFloat(parsed);
        }

        if(isNaN(parsed)) {
            return value.toString(); // TODO: CLEAR OR PUT BACK IN BOUNDS
        }

        const { max, min, decimalPlaces } = this.props.bounds;

        let original = true;

        if(typeof decimalPlaces === "number") {
            const roundMultiplier = Math.pow(10, decimalPlaces);

            // Round down so that the
            const sign = Math.sign(parsed);
            const rounded = sign * Math.floor(Math.abs(parsed) * roundMultiplier) / roundMultiplier;

            if(rounded !== parsed) {
                original = false;
                parsed = rounded;
            }
        }

        if(typeof min === "number" && parsed < min) {
            original = false;
            parsed = min;
        }

        if(typeof max === "number" && parsed > max) {
            original = false;
            parsed = max;
        }

        if(original) {
            return value.toString();
        }

        return parsed.toString();
    }

    protected handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        let value = e.currentTarget.value;

        if(value === ".") {
            value = "0" + value;
        }

        if(value !== "") {
            const parsedValue = parseFloat(value);

            if(isNaN(parsedValue)) {
                return;
            }

            let oldValue = parseFloat(this.props.value);

            if(isNaN(oldValue)) {
                oldValue = 0;
            }

            // Check if this is the first character or the value is already negative and add the negative symbol
            if((oldValue === 0 && this.props.subtract) || this.state.negative) {
                value = "-" + value;
            }

            value = this.mapToBounds(value);
        }

        this.dispatchChange(value);
    }

    protected handleKeyDown(e: React.KeyboardEvent): void {
        e.nativeEvent.stopPropagation();

        if(e.key === "+" || e.key === "=") {
            this.handleIncrement();
        }

        if(e.key === "-") {
            this.handleDecrement();
        }

        if(e.key === "Enter") {
            this.handleConfirm();
        }

        if(e.key === "Escape" && typeof this.props.onEscape === "function") {
            this.props.onEscape(this.props.value);
        }

        const ALLOWED_KEYS = [ ".", "Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab" ];

        if(
            !(
                ALLOWED_KEYS.includes(e.key) ||
                /[0-9]+/g.test(e.key)
            ) || (
                e.key === "." &&
                ("" + this.props.value).indexOf(".") !== -1
            )
        ) {
            e.preventDefault();
        }
    }

    protected handleButton(value: string): void {
        let newValue = this.state.value;
        const oldValue = parseFloat(newValue);

        if(this.resetValue) {
            newValue = "";
            this.resetValue = false;
        } else if(isNaN(oldValue)) {
            // This is to trim leading zeros
            newValue = "";
        }

        if(value === "back") {
            newValue = newValue.slice(0, -1);

            if(newValue === "-") {
                newValue = "";
            }
        } else if(value === "." && newValue.indexOf(".") !== -1) {
            // Do nothing
            return;
        } else {
            if(newValue === "" && value === ".") {
                newValue += "0";
            }

            if(this.props.subtract && (isNaN(oldValue) || oldValue > 0)) {
                newValue = "-" + newValue;
            }

            newValue += value.toString();
        }

        // Bind value to bounds
        newValue = this.mapToBounds(newValue);

        this.dispatchChange(newValue);
    }

    protected handleButtonDown(): void {
        const selection = window.getSelection();

        if(!selection) {
            return;
        }

        if(!selection.anchorNode) {
            return;
        }

        if(selection.type !== "Range") {
            return;
        }

        if(!selection.anchorNode.contains(this.inputRef.current)) {
            return;
        }

        // Remove the selection
        selection.collapseToEnd();

        this.focus();

        this.resetValue = true;
    }

    protected handleClear(): void {
        this.dispatchChange("");

        if(this.inputRef.current) {
            this.inputRef.current.focus();
        }
    }

    protected captureClick(e: React.MouseEvent): void {
        e.stopPropagation();
    }

    protected handleConfirm(): void {
        if(typeof this.props.onConfirm === "function") {
            this.props.onConfirm(this.props.value);
        }
    }

    protected handleDecrement(): void {
        const value: string = this.props.value;
        let amount = parseFloat(value);

        if(value === "") {
            amount = 0;
        } else {
            if(isNaN(amount)) {
                throw new Error(`A NaN was sent to be decremented: ${value}`);
            }
        }

        const fixed = amount.toString().split(".")[1]?.length || 0;

        if(this.props.increment) {
            amount--;
        } else {
            amount = -Math.abs(amount);
        }

        if(fixed > 0) {
            const round = Math.pow(10, fixed);

            amount = Math.round(amount * round) / round;
        }

        this.dispatchChange(this.mapToBounds(amount));
    }

    protected handleIncrement(): void {
        const value = this.props.value;
        let amount = parseFloat(value);

        if(value === "") {
            amount = 0;
        } else {
            if(isNaN(amount)) {
                throw new Error(`A NaN was sent to be incremented ${value}`);
            }
        }

        const fixed = amount.toString().split(".")[1]?.length || 0;

        if(this.props.increment) {
            amount++;
        } else {
            amount = Math.abs(amount);
        }

        if(fixed > 0) {
            const round = Math.pow(10, fixed);

            amount = Math.round(amount * round) / round;
        }

        this.dispatchChange(this.mapToBounds(amount));
    }

    protected dispatchChange(value: string): void {
        if(typeof this.props.onChange === "function") {
            this.props.onChange(value);
        }
    }

    public render(): React.ReactNode {
        const classes = [ "touch-numbers" ];

        if(this.props.increment) {
            classes.push("increment");
        }

        if(this.props.hideConfirm) {
            classes.push("hide-confirm");
        }

        if(this.props.static) {
            classes.push("static");
        }

        let confirm = null;

        if(!this.props.hideConfirm) {
            confirm = (
                <div className="touch-number-confirm">
                    <Button
                        classes={[ "button-touch-confirm" ]}
                        onClick={this.handleConfirm}
                    >
                        {this.props.confirmText}
                    </Button>
                </div>
            );
        }

        let before = "";
        let after = "";

        if(this.state.negative || (!this.state.value && this.props.subtract)) {
            before += "-";
        }

        if(this.props.input === "currency") {
            if(isCurrencySymbolBeforeNumbers()) {
                before += getCurrencySymbol();
            } else {
                after += getCurrencySymbol();
            }
        } else if(this.props.input === "percentage") {
            after += "%";
        }

        return (
            <div
                className={classes.join(" ")}
                onClick={this.captureClick}
                style={{
                    "--touch-before-width": `${before.length === 0 ? 0 : before.length + 1}ch`,
                    "--touch-after-width" : `${before.length === 0 ? 0 : after.length + 1}ch`,
                } as any}
            >
                <div className="flex items-center border-b border-black">
                    {this.props.increment && (
                        <div className="touch-decrement" onClick={this.handleDecrement}>
                            <FontAwesomeLight icon="minus" />
                        </div>
                    )}
                    <div
                        className={
                            "grow flex items-center touch-number-input" + (this.state.focused ? " focused" : "")
                        }
                    >
                        {before !== "" && (
                            <span className="touch-number-input-before">{before}</span>
                        )}
                        <input
                            type={this.props.matchesMedia ? "number" : "text"}
                            value={this.state.value}
                            onKeyDown={this.handleKeyDown}
                            onChange={this.handleChange}
                            onFocus={this.handleFocus}
                            onBlur={this.handleBlur}
                            ref={this.inputRef}
                            className="grow"
                            data-testid="touch-number-input"
                        />
                        {after !== "" && (
                            <span className="touch-number-input-after">{after}</span>
                        )}
                        {!!this.props.value && (
                            <span
                                className="touch-number-input-clear"
                                onClick={this.handleClear}
                            >
                                <FontAwesomeLight icon="times" />
                            </span>
                        )}
                    </div>
                    {this.props.increment && (
                        <div className="touch-increment" onClick={this.handleIncrement}>
                            <FontAwesomeLight icon="plus" />
                        </div>
                    )}
                </div>
                <div className="touch-number-buttons">
                    <TouchNumber onClick={this.handleButton} onMouseDown={this.handleButtonDown} value={"1"} />
                    <TouchNumber onClick={this.handleButton} onMouseDown={this.handleButtonDown} value={"2"} />
                    <TouchNumber onClick={this.handleButton} onMouseDown={this.handleButtonDown} value={"3"} />
                    <TouchNumber onClick={this.handleButton} onMouseDown={this.handleButtonDown} value={"4"} />
                    <TouchNumber onClick={this.handleButton} onMouseDown={this.handleButtonDown} value={"5"} />
                    <TouchNumber onClick={this.handleButton} onMouseDown={this.handleButtonDown} value={"6"} />
                    <TouchNumber onClick={this.handleButton} onMouseDown={this.handleButtonDown} value={"7"} />
                    <TouchNumber onClick={this.handleButton} onMouseDown={this.handleButtonDown} value={"8"} />
                    <TouchNumber onClick={this.handleButton} onMouseDown={this.handleButtonDown} value={"9"} />
                    <TouchNumber onClick={this.handleButton} onMouseDown={this.handleButtonDown} value={"."} />
                    <TouchNumber onClick={this.handleButton} onMouseDown={this.handleButtonDown} value={"0"} />
                    <TouchNumber onClick={this.handleButton} onMouseDown={this.handleButtonDown} value={"back"} />
                </div>
                {confirm}
            </div>
        );
    }
}

export default withMediaQuery(TouchNumbers, "(max-width: 700px)");
